import { supabase } from "@/integrations/supabase/client";
import { PostCategory, CreatePostCategoryData, UpdatePostCategoryData } from "@/types/community";

// ===============================
// BUSCAR CATEGORIAS DE UMA COMUNIDADE
// ===============================

export const fetchCommunityCategories = async (communityId: string): Promise<PostCategory[]> => {
  try {
    console.log('📋 [fetchCommunityCategories] Buscando categorias para comunidade:', communityId);

    const { data, error } = await supabase
      .from('community_post_categories')
      .select('*')
      .eq('community_id', communityId)
      .order('name', { ascending: true });

    if (error) {
      console.error('❌ [fetchCommunityCategories] Erro na query:', error);
      // Se a tabela não existe, retornar array vazio
      if (error.message?.includes('does not exist') || error.code === 'PGRST116') {
        console.warn('⚠️ [fetchCommunityCategories] Tabela community_post_categories não existe ainda');
        return [];
      }
      throw error;
    }

    console.log('✅ [fetchCommunityCategories] Categorias encontradas:', data?.length || 0);
    return data || [];

  } catch (error) {
    console.error('❌ [fetchCommunityCategories] Erro ao buscar categorias:', error);
    return [];
  }
};

// ===============================
// CRIAR CATEGORIA
// ===============================

export const createPostCategory = async (categoryData: CreatePostCategoryData): Promise<PostCategory> => {
  try {
    console.log('📝 [createPostCategory] Criando categoria:', categoryData);

    // Verificar se o usuário é dono da comunidade
    const { data: community, error: communityError } = await supabase
      .from('communities')
      .select('mentor_id, owner_id')
      .eq('id', categoryData.community_id)
      .single();

    if (communityError || !community) {
      throw new Error('Comunidade não encontrada');
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Usuário não autenticado');

    const canCreate = community.mentor_id === user.id || community.owner_id === user.id;
    if (!canCreate) {
      throw new Error('Você não tem permissão para criar categorias nesta comunidade');
    }

    // Criar categoria
    const { data, error } = await supabase
      .from('community_post_categories')
      .insert([{
        name: categoryData.name,
        description: categoryData.description,
        community_id: categoryData.community_id,
        color: categoryData.color || '#00B6F0'
      }])
      .select()
      .single();

    if (error) {
      console.error('❌ [createPostCategory] Erro ao criar categoria:', error);
      throw error;
    }

    console.log('✅ [createPostCategory] Categoria criada:', data.id);
    return data;

  } catch (error) {
    console.error('❌ [createPostCategory] Erro geral:', error);
    throw error;
  }
};

// ===============================
// ATUALIZAR CATEGORIA
// ===============================

export const updatePostCategory = async (categoryId: string, updateData: UpdatePostCategoryData): Promise<PostCategory> => {
  try {
    console.log('📝 [updatePostCategory] Atualizando categoria:', categoryId, updateData);

    const { data, error } = await supabase
      .from('community_post_categories')
      .update(updateData)
      .eq('id', categoryId)
      .select()
      .single();

    if (error) {
      console.error('❌ [updatePostCategory] Erro ao atualizar categoria:', error);
      throw error;
    }

    console.log('✅ [updatePostCategory] Categoria atualizada:', data.id);
    return data;

  } catch (error) {
    console.error('❌ [updatePostCategory] Erro geral:', error);
    throw error;
  }
};

// ===============================
// DELETAR CATEGORIA
// ===============================

export const deletePostCategory = async (categoryId: string): Promise<void> => {
  try {
    console.log('🗑️ [deletePostCategory] Deletando categoria:', categoryId);

    // Verificar se há posts usando esta categoria
    const { data: posts, error: postsError } = await supabase
      .from('community_posts')
      .select('id')
      .eq('category_id', categoryId)
      .limit(1);

    if (postsError) {
      console.warn('⚠️ [deletePostCategory] Erro ao verificar posts:', postsError);
    }

    if (posts && posts.length > 0) {
      throw new Error('Não é possível deletar uma categoria que possui posts. Mova os posts para outra categoria primeiro.');
    }

    const { error } = await supabase
      .from('community_post_categories')
      .delete()
      .eq('id', categoryId);

    if (error) {
      console.error('❌ [deletePostCategory] Erro ao deletar categoria:', error);
      throw error;
    }

    console.log('✅ [deletePostCategory] Categoria deletada com sucesso');

  } catch (error) {
    console.error('❌ [deletePostCategory] Erro geral:', error);
    throw error;
  }
}; 